/*
 * Copyright (C) Motorola Solutions, INC.
 * All Rights Reserved.
 */

import { AuxliaryOutputsNames, DeviceData, WebUsbModuleVersion } from 'web-usb-module';
import { deviceStateUpdate } from '../../../../call/+state/cchub.actions';
import { WebUsbManager } from './web-usb-manager';
import { interval, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { Mutex } from 'async-mutex';

export class WebPeripheralNotifier {
    private static readonly KEEP_ALIVE_PERIOD = 500;
    protected webUsbManager: WebUsbManager;
    protected webUsbDeviceData: DeviceData;
    protected onCCHconnectionStateChangedCallback: ((isConnected: boolean) => void)[];
    private readonly readSerialNumberMutex: Mutex;
    private onCCHerrorCallback: ((message: any) => any)[];
    private keepAliveTimer: Subscription;
    private keepAlivesStarted: boolean;
    private isConnected: boolean;
    private store: Store;
    private cchubSerialNumber: string;

    public constructor(store: Store) {
        this.store = store;
        this.onCCHconnectionStateChangedCallback = [];
        this.readSerialNumberMutex = new Mutex();
        this.cchubSerialNumber = '';
        this.init();
    }

    private init() {
        this.setupMembers();
        this.onCCHerrorCallback = [];
        this.keepAlivesStarted = false;
        this.isConnected = false;
        this.webUsbManager.init((data) => {
            this.store.dispatch(deviceStateUpdate({ deviceData: structuredClone(data) }));
        }, (error) => {
            this.onCCHerrorCallback.forEach((callback) => callback(error.message));
        });
    }

    protected setupMembers() {
        this.webUsbManager = new WebUsbManager();
        this.webUsbDeviceData = new DeviceData();
    }

    private sendKeepAlive() {
        return this.webUsbManager.sendRPIKeepAlive();
    }

    getVersion() {
        return WebUsbModuleVersion;
    }
    /**
     * Starts sending keep alives to CCH. Default period is `500 ms`
     */
    startSendingKeepAlives() {
        if (this.keepAlivesStarted === false) {
            this.keepAliveTimer = interval(WebPeripheralNotifier.KEEP_ALIVE_PERIOD).subscribe(() => this.sendKeepAlive());
            this.keepAlivesStarted = true;
        }
    }
    /**
     * Stops sending keep alives to CCH
     */
    stopSendingKeepAlives() {
        if (this.keepAlivesStarted) {
            this.keepAliveTimer.unsubscribe();
            this.keepAlivesStarted = false;
        }
    }
    /**
     * Returns state of all auxiliaries
     */
    getAuxiliaryOutputs() {
        if (this.isConnected) {
            return this.webUsbManager.getAuxiliaryOutputs().getStatesOfAllOutputs();
        }
        return {};
    }

    /**
     * Function calls when error on CCH occured
     * @param callback
     */
    onCCHubError(callback: any) {
        this.onCCHerrorCallback.push(callback);
    }
    /**
     * Stops sending keep alives and closes USB connection
     */
    closeWebUsbConnection() {
        if (this.keepAlivesStarted === true) {
            this.keepAliveTimer.unsubscribe();
            this.keepAlivesStarted = false;
        }
        return this.webUsbManager.stop();
    }
    /**
     * Change CCH auxiliary port state
     * @param name Name of auxiliary port
     * @param value State of auxiliary port
     */
    sendOutputs(name: AuxliaryOutputsNames, value: boolean) {
        return this.webUsbManager.sendOutputs(name, value);
    }
    /**
     * Initializes USB connection to CCH
     */
    async startCCHub() {
        await this.webUsbManager.connectToDevice();
        this.webUsbManager.finalizeConnection();
        this.startSendingKeepAlives();
        this.isConnected = true;
        // enable microphone (sendOutputs after keepalives starts CONSOLE-32372)
        setTimeout(() => {
            this.webUsbManager.sendOutputs(AuxliaryOutputsNames.Desktop_microphone_Bias_current, true);
        }, WebPeripheralNotifier.KEEP_ALIVE_PERIOD);
    }
    /**
     * Stops keep alives and disconnects CCH
     */
    disconnectCCHub() {
        console.log('Disconnecting CCHub...');
        return Promise.all([
                this.webUsbManager.sendOutputs(AuxliaryOutputsNames.Auxiliary_output_1, false),
                this.webUsbManager.sendOutputs(AuxliaryOutputsNames.Auxiliary_output_2, false),
                this.webUsbManager.sendOutputs(AuxliaryOutputsNames.Auxiliary_output_3, false),
                this.webUsbManager.sendOutputs(AuxliaryOutputsNames.Auxiliary_output_4, false)
            ])
            .catch((e) => console.error(`Failed to switch off one or more aux outputs`, e))
            .then(() => {
                if (this.keepAlivesStarted === true) {
                    this.keepAliveTimer.unsubscribe();
                    this.keepAlivesStarted = false;
                }
            })
            .then(() => this.webUsbManager.disconnect())
            .then(() => {
                this.isConnected = false;
                this.cchubSerialNumber = '';
            });
    }
    /**
     * Stop processing any WebUSB events and disconnect device
     */
    stop() {
        return this.webUsbManager.stop();
    }
}
